import { useEffect, useState } from "react";
import { useAuth } from "../../../store/useAuth";
import { PropertyEndpoint } from "../../../api";
import { Card, Row, Col, Spin, Alert, Table, Button, Space } from "antd";
import { FileExcelOutlined, FilePdfOutlined } from "@ant-design/icons";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const formatRupiah = (value) =>
    new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        minimumFractionDigits: 0,
    }).format(value);

function Dashboard() {
    const { currentUser } = useAuth();
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const propertyApi = new PropertyEndpoint();

    useEffect(() => {
        const fetchDashboard = async () => {
            setLoading(true);
            setError(null);

            try {
                const res = await propertyApi.dashboardProperty();
                setDashboardData(res?.data || res);
            } catch (err) {
                console.error("Failed to fetch dashboard:", err);
                setError("Failed to load dashboard data.");
            } finally {
                setLoading(false);
            }
        };

        fetchDashboard();
    }, []);

    // Table columns for transactions
    const transactionColumns = [
        {
            title: 'Transaction ID',
            dataIndex: 'id',
            key: 'id',
            width: 200,
            ellipsis: true,
        },
        {
            title: 'Property',
            dataIndex: 'property',
            key: 'property',
            render: (property) => property?.name || '-',
            width: 150,
        },
        {
            title: 'Type',
            dataIndex: 'transaction_type',
            key: 'transaction_type',
            render: (type) => type?.charAt(0).toUpperCase() + type?.slice(1) || '-',
            width: 100,
        },
        {
            title: 'Amount',
            dataIndex: 'nominal',
            key: 'nominal',
            render: (amount) => formatRupiah(amount),
            width: 120,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <span style={{
                    color: status === 'Verified' ? '#52c41a' :
                        status === 'Pending' ? '#faad14' : '#ff4d4f'
                }}>
                    {status}
                </span>
            ),
            width: 100,
        },
        {
            title: 'Transaction Date',
            dataIndex: 'transaction_date',
            key: 'transaction_date',
            render: (date) => {
                if (!date) return '-';
                const dateObj = new Date(typeof date === 'number' ? date : date);
                return dateObj.toLocaleDateString('id-ID', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
            },
            width: 150,
        },
        {
            title: 'Created By',
            dataIndex: 'createdByUser',
            key: 'createdByUser',
            render: (user) => user?.name || '-',
            width: 120,
        },
        {
            title: 'Verified By',
            dataIndex: 'verifiedByUser',
            key: 'verifiedByUser',
            render: (user) => user?.name || '-',
            width: 120,
        },
    ];

    // Export to Excel function
    const exportToExcel = () => {
        if (!dashboardData?.transactionsManaged?.length) return;

        const dataToExport = dashboardData.transactionsManaged.map(transaction => ({
            'Transaction ID': transaction.id,
            'Property Name': transaction.property?.name || '-',
            'Property Province': transaction.property?.province || '-',
            'Property City': transaction.property?.city || '-',
            'Transaction Type': transaction.transaction_type || '-',
            'Amount': transaction.nominal,
            'Status': transaction.status,
            'Transaction Date': transaction.transaction_date ?
                new Date(typeof transaction.transaction_date === 'number' ?
                    transaction.transaction_date : transaction.transaction_date)
                    .toLocaleDateString('id-ID') : '-',
            'Created By': transaction.createdByUser?.name || '-',
            'Created By Email': transaction.createdByUser?.email || '-',
            'Verified By': transaction.verifiedByUser?.name || '-',
            'Verified By Email': transaction.verifiedByUser?.email || '-',
            'Expired Date': transaction.expired_date || '-',
        }));

        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Transactions");
        XLSX.writeFile(wb, `transactions_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    // Export to PDF function
    const exportToPDF = async () => {
        if (!dashboardData?.transactionsManaged?.length) return;

        const tableElement = document.querySelector('.transactions-table');
        if (!tableElement) return;

        try {
            const canvas = await html2canvas(tableElement, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');

            const imgWidth = 210;
            const pageHeight = 295;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            let heightLeft = imgHeight;

            let position = 0;

            pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;

            while (heightLeft >= 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
            }

            pdf.save(`transactions_${new Date().toISOString().split('T')[0]}.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Error generating PDF. Please try again.');
        }
    };

    return (
        <div className="ltn__myaccount-tab-content-inner">
            <p>
                Hello <strong>{currentUser?.name}</strong>
            </p>

            {loading && <Spin />}
            {error && <Alert type="error" message={error} showIcon />}

            {dashboardData && (
                <>
                    <Row gutter={16} className="mt-3">
                        <Col xs={24} md={12}>
                            <Card title="Total Properties">
                                <h2 style={{ margin: 0 }}>
                                    {dashboardData.total_properties}
                                </h2>
                            </Card>
                        </Col>

                        <Col xs={24} md={12}>
                            <Card title="Total Earnings">
                                <h2 style={{ margin: 0 }}>
                                    {formatRupiah(dashboardData.total_earnings)}
                                </h2>
                            </Card>
                        </Col>
                    </Row>

                    {/* Transactions Section */}
                    <div style={{ marginTop: '24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h3 style={{ margin: 0 }}>Transaction History</h3>
                            <Space>
                                <Button
                                    type="primary"
                                    icon={<FileExcelOutlined />}
                                    onClick={exportToExcel}
                                    disabled={!dashboardData?.transactionsManaged?.length}
                                >
                                    Export Excel
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<FilePdfOutlined />}
                                    onClick={exportToPDF}
                                    disabled={!dashboardData?.transactionsManaged?.length}
                                >
                                    Export PDF
                                </Button>
                            </Space>
                        </div>

                        <Table
                            className="transactions-table"
                            columns={transactionColumns}
                            dataSource={dashboardData?.transactionsManaged || []}
                            rowKey="id"
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} of ${total} transactions`
                            }}
                            scroll={{ x: 1200 }}
                            size="middle"
                        />
                    </div>
                </>
            )}
        </div>
    );
}

export default Dashboard;